import { useMemo } from "react";
import useSWR from "swr";
import { useIntegrations } from "@/api/integration";
import { type EnhancedDataItem, MarianaTekService } from "@/api/mariana-tek";
import type { Customer } from "@/api/types";
import { getCustomerSource } from "@/lib/utils";
import useActiveGym from "@/ui/hooks/active-gym";

const SWR_OPTIONS = { revalidateOnFocus: false } as const;

function useMarianaTekIntegrationForCustomer(customer?: Customer) {
  const { gym } = useActiveGym();
  const { data: integrations } = useIntegrations({ gym: gym?.id });
  const customerSource = customer ? getCustomerSource(customer) : null;
  const integration = integrations?.find((i) => i.type === customerSource);
  const mtId = customer?.properties?.mariana_tek?.id;
  return { integration, mtId };
}

export function useCustomerDetails(customer?: Customer) {
  const { integration, mtId } = useMarianaTekIntegrationForCustomer(customer);
  const integrationId = integration?.id;
  const key = integrationId && mtId ? `mt-user/${integrationId}/${mtId}` : null;

  // biome-ignore lint/suspicious/noExplicitAny: Mariana Tek RPC returns untyped JSON
  const { data } = useSWR<any>(
    key,
    async () => {
      const service = new MarianaTekService(integrationId!);
      return service.getUser(mtId!);
    },
    SWR_OPTIONS,
  );

  return { data };
}

export function useCustomerMemberships(customer?: Customer) {
  const { integration, mtId } = useMarianaTekIntegrationForCustomer(customer);
  const integrationId = integration?.id;
  const key = integrationId && mtId ? `mt-memberships/${integrationId}/${mtId}` : null;

  const { data } = useSWR<EnhancedDataItem[]>(
    key,
    async () => {
      const service = new MarianaTekService(integrationId!);
      return service.getUserMemberships(mtId!) as Promise<EnhancedDataItem[]>;
    },
    SWR_OPTIONS,
  );

  return { data };
}

export function useMarianaTekLocations() {
  const { gym } = useActiveGym();
  const { data: integrations } = useIntegrations({ gym: gym?.id });
  const integration = integrations?.find((i) => i.type === "mariana-tek");
  const integrationId = integration?.id;
  const key = integrationId ? `mt-locations/${integrationId}` : null;

  // biome-ignore lint/suspicious/noExplicitAny: Mariana Tek RPC returns untyped JSON
  const { data, isLoading } = useSWR<any[]>(
    key,
    async () => {
      const service = new MarianaTekService(integrationId!);
      return service.getLocations() as Promise<any[]>;
    },
    SWR_OPTIONS,
  );

  // No key means the integration list hasn't resolved yet — still loading.
  return { data, isLoading: key === null || isLoading };
}

export function useCustomerReservations(customer?: Customer) {
  const { integration, mtId } = useMarianaTekIntegrationForCustomer(customer);
  const integrationId = integration?.id;
  const key = integrationId && mtId ? `mt-reservations/${integrationId}/${mtId}` : null;

  const { data } = useSWR<EnhancedDataItem[]>(
    key,
    async () => {
      const service = new MarianaTekService(integrationId!);
      return service.getUserReservations(mtId!) as Promise<EnhancedDataItem[]>;
    },
    SWR_OPTIONS,
  );

  return { data };
}

/** Location options plus the loading flag, so callers can tell loading from empty. */
export function useMarianaTekLocationOptions() {
  const { data: locations, isLoading } = useMarianaTekLocations();

  const options = useMemo(
    () =>
      locations?.map((location: any) => ({
        label: location.attributes.name,
        value: location.id,
      })) ?? [],
    [locations],
  );

  return { options, isLoading };
}

export function useMarianaTekIntegration() {
  const { gym } = useActiveGym();
  const { data: integrations } = useIntegrations({ gym: gym?.id });
  return integrations?.find((i) => i.type === "mariana-tek");
}
